import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { trackSegment } from '~/components/SegmentAnalytics';
import { FieldIcon } from '~/components/Shared/Filters_DEPRECATED/FieldIcon';
import { OperatorSelect } from '~/components/Shared/Filters_DEPRECATED/OperatorSelect';
import { TextContainer } from '~/components/Shared/Filters_DEPRECATED/TextContainer';
import { Box } from '~/eds/src/components/Box/Box';
import { Dropdown } from '~/eds/src/components/Dropdown/Dropdown';
import { DropdownActionsFooter } from '~/eds/src/components/Dropdown/subcomponents/DropdownActionsFooter';
import { DropdownBody } from '~/eds/src/components/Dropdown/subcomponents/DropdownBody';
import { DropdownSelectHeader } from '~/eds/src/components/Dropdown/subcomponents/DropdownSelectHeader';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Nullable as EdsNullable } from '~/eds/src/types/Nullable';
import { Filter } from '~/evifields/src/types/Filter';
import { FilterViewType } from '~/evifields/src/types/FilterView';
import { OperatorId } from '~/evifields/src/types/OperatorId';
import { AsyncValue, Value } from '~/evifields/src/types/Value';
import { getFilterValuesLength } from '~/evifields/src/utils/getFilterValuesLength';
import { testIsActiveFilter } from '~/evifields/src/utils/testIsActiveFilter';
import { testIsEmptyFilter } from '~/evifields/src/utils/testIsEmptyFilter';
import { DOCUMENT_ID_FILTER } from '~/features/filters/constants';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { SearchFilter } from '~/types/DashboardV2';
import { Nullable } from '~/types/Nullable';

import Values from '../Values/Values';
import ValuesPreview from '../ValuesPreview/ValuesPreview';
import {
  clearFilter,
  clearFilterValues,
  isValidFilterValue,
  updateFilterOperator,
  updateFilterValues,
} from './FilterChip.utils';

type Props = {
  enableFilterViews?: boolean;
  disableRemove: boolean;
  disableEdit?: boolean;
  isPinned?: boolean;
  searchFilter: SearchFilter;
  filter: Filter;
  readOnly?: boolean;
  onChange: (_updatedFilter: Filter) => void;
  onRemove?: (_filterToRemove: Filter) => void;
  hasError?: boolean;
};

const FilterChip = ({
  enableFilterViews = false,
  disableRemove,
  disableEdit,
  isPinned = false,
  searchFilter,
  filter: initialFilter,
  readOnly,
  onChange: onCommitChange,
  onRemove,
  hasError,
}: Props) => {
  const intl = useIntl();
  const [filter, setFilter] = useState(initialFilter);
  useEffect(() => setFilter(initialFilter), [initialFilter]);

  const isFilterChipApplyButton = useFlag(FlagType.FilterChipApplyButton);
  const triggerFilter = isFilterChipApplyButton ? initialFilter : filter;

  const [defaultOperatorId, setDefaultOperatorId] = useState<
    Nullable<OperatorId>
  >(initialFilter.operatorId);

  const [isVisible, _toggle, show, hide] = useToggle();
  const user = useCurrentUser();

  const { label, type, operators } = searchFilter;
  const { operatorId } = filter;

  const hasOperator = Boolean(operatorId);
  const triggerHasValues = getFilterValuesLength(triggerFilter) > 0;

  const operator = operators.find((op) => op.id === operatorId);

  const isAllBooleanOperator = operators.every(
    (operator) => operator.cardinality === 0,
  );
  const hasOperators = operators.length >= 1;

  const isFolderField = type === 'folder';
  const isEmpty = testIsEmptyFilter(filter);
  const isBooleanTextSearch = type === 'bool_text_search';

  const isTriggerActive = Boolean(
    (operator && testIsActiveFilter(triggerFilter, operator, type)) ||
    (isBooleanTextSearch && triggerHasValues),
  );
  const isEmptyValue =
    (operator?.cardinality || 0) > 0 && getFilterValuesLength(filter) === 0;
  const styles = useStyles();

  const handleRemove = () => {
    if (isEmpty || disableEdit) {
      onRemove?.(filter);
    } else {
      const updateFilter = hasOperators ? clearFilter : clearFilterValues;
      const updatedFilter = updateFilter(filter);
      setFilter(updatedFilter);
      onCommitChange(updatedFilter);
    }
    hide();
  };

  const handleUpdateOperator = (updatedOperatorId: Nullable<OperatorId>) => {
    const updatedFilter = updateFilterOperator(filter, updatedOperatorId);
    setFilter(updatedFilter);

    if (!isFilterChipApplyButton) {
      const updatedOperator = operators.find(
        (op) => op.id === updatedOperatorId,
      );
      if (updatedOperator?.cardinality === 0) {
        hide();
      }
    }
  };

  const handleUpdateValues = (
    updatedValues: Value[],
    updatedAsyncValue?: AsyncValue<Value>,
  ) => {
    const updatedFilter = updateFilterValues(
      filter,
      updatedValues,
      updatedAsyncValue,
    );
    setFilter(updatedFilter);

    if (
      !isFilterChipApplyButton &&
      (type === 'enum' ||
        (type === 'enum_set' && filter.filterView === 'text-delimited') ||
        (type === 'text' && searchFilter.label === DOCUMENT_ID_FILTER))
    ) {
      hide();
    }
  };

  const handleUpdateFilterView = (filterView: EdsNullable<FilterViewType>) => {
    setFilter({
      ...clearFilterValues(filter),
      filterView: filterView ?? undefined,
    });
  };

  const handleCancel = () => {
    setFilter(initialFilter);
    hide();
  };

  const handleHide = () => {
    const filterToCommit = isEmptyValue ? clearFilter(filter) : filter;
    setFilter(filterToCommit);
    if (
      !isEqual(
        {
          operatorId: initialFilter.operatorId,
          values: initialFilter.values,
        },
        {
          operatorId: filterToCommit.operatorId,
          values: filterToCommit.values,
        },
      ) &&
      isValidFilterValue(filterToCommit, searchFilter)
    ) {
      onCommitChange(filterToCommit);
    }

    hide();
  };

  const handleShow = () => {
    if (!disableEdit) {
      if (!hasOperator && !isAllBooleanOperator) {
        const firstOperator = operators[0];
        setFilter(updateFilterOperator(filter, firstOperator.id));
        setDefaultOperatorId(firstOperator.id);
      }
      trackSegment('openFilter', {
        fieldId: filter.fieldId,
        name: searchFilter.label,
        user_id: user.id,
        client_id: user.client,
      });
      show();
    }
  };

  const handleClearFilter = () => {
    const emptyFilter = clearFilter(filter);
    setFilter(emptyFilter);
    onCommitChange(emptyFilter);
    hide();
  };

  const handleApplyFilter = () => {
    const filterToCommit = isEmptyValue ? clearFilter(filter) : filter;
    onCommitChange(filterToCommit);
    hide();
  };

  const disableHeader = isAllBooleanOperator || isFolderField || !hasOperators;

  const header = !disableHeader && (
    <DropdownSelectHeader
      select={
        <OperatorSelect
          operators={operators}
          value={operatorId}
          onChange={handleUpdateOperator}
        />
      }
    />
  );

  let contents;
  if (operator?.cardinality || isBooleanTextSearch) {
    contents = (
      <DropdownBody>
        <Values
          field={searchFilter}
          filter={filter}
          onChange={handleUpdateValues}
          onViewChange={enableFilterViews ? handleUpdateFilterView : undefined}
        />
      </DropdownBody>
    );
  } else if (isAllBooleanOperator) {
    contents = (
      <OperatorSelect
        isEmbedded={isAllBooleanOperator}
        operators={operators}
        value={operatorId}
        onChange={handleUpdateOperator}
      />
    );
  }

  const footer = isFilterChipApplyButton && (
    <DropdownActionsFooter
      leftActions={[
        {
          text: intl.formatMessage({
            id: 'Filters.FilterChip.cancel',
            defaultMessage: 'Cancel',
            description: 'Button to close filter dropdown without saving',
          }),
          level: 'tertiary',
          onClick: handleCancel,
        },
      ]}
      rightActions={[
        {
          text: intl.formatMessage({
            id: 'Filters.FilterChip.clear',
            defaultMessage: 'Clear',
            description: 'Button to clear filter values',
          }),
          level: 'tertiary',
          onClick: handleClearFilter,
        },
        {
          text: intl.formatMessage({
            id: 'Filters.FilterChip.apply',
            defaultMessage: 'Apply',
            description: 'Button to apply filter changes',
          }),
          level: 'action',
          onClick: handleApplyFilter,
          disabled:
            isEqual(
              {
                operatorId: defaultOperatorId,
                values: initialFilter.values,
              },
              {
                operatorId: filter.operatorId,
                values: filter.values,
              },
            ) || !isValidFilterValue(filter, searchFilter),
        },
      ]}
    />
  );

  const initialOperator = operators.find(
    (op) => op.id === initialFilter.operatorId,
  );
  const triggerOperator = isFilterChipApplyButton ? initialOperator : operator;
  const filterText = (
    <Layout spacing={1}>
      <Box minW="min-content">
        <TextContainer isBold={false} text={label} />
      </Box>
      {triggerOperator && <strong> {triggerOperator.label}</strong>}
      {triggerHasValues && (
        <ValuesPreview field={searchFilter} filter={triggerFilter} />
      )}
    </Layout>
  );

  const trigger = (
    <Layout
      align="center"
      styles={[componentStyles.base, readOnly ? null : styles.colors.status]}
      styleProps={{
        status: hasError ? 'danger' : isTriggerActive ? 'active' : undefined,
      }}
    >
      <Layout
        align="center"
        as="button"
        borderRadius="m"
        color="inherit"
        h="100%"
        minW={0}
        px={2}
        spacing={2}
        styles={[
          styles.button.unset,
          componentStyles.leftContent,
          disableEdit || readOnly ? componentStyles.disabled : {},
        ]}
        onClick={readOnly ? undefined : handleShow}
      >
        {isPinned ? (
          <Icon icon={triggerHasValues ? 'push-pin-filled' : 'push-pin'} />
        ) : (
          <FieldIcon fieldType={type} />
        )}

        <TruncateText color="text.primary" variant="tiny" whiteSpace="nowrap">
          {filterText}
        </TruncateText>
        {!readOnly && disableRemove && (
          <Icon
            color="currentColor"
            icon={isVisible ? 'chevron-up' : 'chevron-down'}
            label={
              isVisible
                ? intl.formatMessage({
                    id: 'FilterChip.expandedFieldLabel',
                    defaultMessage: 'Expanded field',
                    description: 'ARIA label for expanded filter field',
                  })
                : intl.formatMessage({
                    id: 'FilterChip.collapsedFieldLabel',
                    defaultMessage: 'Collapsed field',
                    description: 'ARIA label for collapsed filter field',
                  })
            }
          />
        )}
        {!readOnly && !disableRemove && (
          <IconButton
            icon="x"
            size="s"
            tooltip={
              isEmpty || disableEdit
                ? intl.formatMessage({
                    id: 'FilterChip.removeFilterTooltip',
                    defaultMessage: 'Remove filter',
                    description: 'Tooltip for removing filter',
                  })
                : intl.formatMessage({
                    id: 'Filters.FilterChip.clearValuesTooltip',
                    defaultMessage: 'Clear values',
                    description: 'Tooltip for clearing filter values',
                  })
            }
            onClick={handleRemove}
          />
        )}
      </Layout>
    </Layout>
  );

  if (readOnly) {
    return trigger;
  }

  return (
    <Dropdown
      header={header}
      isVisible={isVisible}
      trigger={trigger}
      triggerMode="shrink"
      onClickOutside={hide}
      onHide={handleHide}
      onShow={handleShow}
      footer={footer}
    >
      {isVisible && contents}
    </Dropdown>
  );
};

export const componentStyles = {
  base: {
    backgroundColor: 'background',
    border: 'border',
    borderRadius: 'm',
    height: 'filter.height',
    outline: 'none',
    maxWidth: '100%',
    width: 'min-content',
  },
  leftContent: {
    borderTopLeftRadius: 'm',
    borderBottomLeftRadius: 'm',
    flex: '0 1 auto',
  },
  disabled: {
    opacity: '50%',
  },
};

export default FilterChip;
